import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import api from '../../utils/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { Field, Input, FormActions } from '../components/FormField';

// ── Colour swatch palette ────────────────────────────────────────────────────
const SWATCHES = [
  // Neutrals
  '#000000','#1A1A1A','#2D2D2D','#4A4A4A','#6B6B6B','#9CA3AF','#D1D5DB','#F3F4F6','#FFFFFF',
  // Warm
  '#7C2D12','#9A3412','#C2410C','#EA580C','#F97316','#FB923C','#FED7AA','#FEF3C7',
  // Gold / Brand
  '#78350F','#92400E','#B45309','#B8A830','#D4AF37','#D97706','#F59E0B','#FCD34D',
  // Green
  '#14532D','#166534','#15803D','#16A34A','#22C55E','#4ADE80','#86EFAC','#DCFCE7',
  // Blue
  '#1E3A5F','#1E40AF','#2563EB','#3B82F6','#60A5FA','#93C5FD','#BFDBFE','#EFF6FF',
  // Purple / Pink
  '#4C1D95','#6D28D9','#7C3AED','#8B5CF6','#A78BFA','#EC4899','#F472B6','#FCE7F3',
  // Red
  '#7F1D1D','#991B1B','#B91C1C','#DC2626','#EF4444','#F87171','#FCA5A5','#FEE2E2',
];

function ColorSwatchPicker({ value, onChange, label, hint }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}
      {/* Trigger button — shows current colour */}
      <button type="button" onClick={() => setOpen(o => !o)}
        className="flex items-center gap-3 w-full border-2 border-gray-200 hover:border-yellow-400 rounded-xl px-3 py-2.5 transition-colors bg-white">
        <span className="w-7 h-7 rounded-lg border border-gray-200 shrink-0 shadow-sm"
          style={{ background: value }} />
        <span className="text-sm font-mono text-gray-600">{value}</span>
        <span className="ml-auto text-gray-400 text-xs">▾</span>
      </button>

      {/* Dropdown palette */}
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-2xl shadow-xl p-4 w-72">
          <div className="grid grid-cols-9 gap-1.5 mb-3">
            {SWATCHES.map(c => (
              <button key={c} type="button"
                onClick={() => { onChange(c); setOpen(false); }}
                title={c}
                className={`w-6 h-6 rounded-md border-2 transition-transform hover:scale-110 ${value === c ? 'border-yellow-500 scale-110' : 'border-transparent'}`}
                style={{ background: c, boxShadow: c === '#FFFFFF' ? 'inset 0 0 0 1px #e5e7eb' : undefined }}
              />
            ))}
          </div>
          {/* Custom colour picker */}
          <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
            <label className="text-xs text-gray-500 shrink-0">Custom:</label>
            <input type="color" value={value}
              onChange={e => onChange(e.target.value)}
              className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer p-0.5 shrink-0" />
            <input type="text" value={value}
              onChange={e => { if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) onChange(e.target.value); }}
              className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-yellow-400" />
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_TYPOGRAPHY = {
  bodyFont:    'Inter',
  headingFont: 'Playfair Display',
  baseColor:   '#1A1A1A',
  accentColor: '#B8A830',
  baseFontSize:'16',
  fontStyle:   'normal',
};

const BODY_FONTS    = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Poppins', 'Nunito', 'Source Sans Pro', 'Raleway'];
const HEADING_FONTS = ['Playfair Display', 'Merriweather', 'Lora', 'Cormorant Garamond', 'EB Garamond', 'Libre Baskerville', 'Josefin Sans', 'Montserrat'];

// Supported platforms with their display names and brand colours
const PLATFORM_OPTIONS = [
  { value: 'instagram', label: 'Instagram',  color: '#E1306C', placeholder: 'https://instagram.com/yourhandle' },
  { value: 'linkedin',  label: 'LinkedIn',   color: '#0A66C2', placeholder: 'https://linkedin.com/company/...' },
  { value: 'facebook',  label: 'Facebook',   color: '#1877F2', placeholder: 'https://facebook.com/yourpage' },
  { value: 'youtube',   label: 'YouTube',    color: '#FF0000', placeholder: 'https://youtube.com/@yourchannel' },
  { value: 'twitter',   label: 'X / Twitter',color: '#000000', placeholder: 'https://x.com/yourhandle' },
  { value: 'whatsapp',  label: 'WhatsApp',   color: '#25D366', placeholder: 'https://wa.me/919876543210' },
  { value: 'pinterest', label: 'Pinterest',  color: '#E60023', placeholder: 'https://pinterest.com/yourprofile' },
];

// SVG icons for each platform
const PlatformIcon = ({ platform, size = 16 }) => {
  if (!platform) return null;
  const icons = {
    instagram: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
    linkedin:  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    facebook:  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    youtube:   <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>,
    twitter:   <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    whatsapp:  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
    pinterest: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>,
  };
  return icons[platform] || <span className="text-xs font-bold uppercase">{platform ? platform[0] : '?'}</span>;
};

const getPlatformInfo = (value) => PLATFORM_OPTIONS.find(p => p.value === value) || { label: value, color: '#6b7280', placeholder: 'https://...' };

export default function AdminSiteSettings() {
  const { settings, refresh } = useSiteSettings();
  const [form, setForm] = useState({
    siteName: '', tagline: '',
    'contact.address': '', 'contact.mapUrl': '', 'contact.phone': '', 'contact.email': '', 'contact.hours': '',
  });
  const [socialLinks, setSocialLinks] = useState([]);
  const [typography, setTypography]   = useState(DEFAULT_TYPOGRAPHY);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addPlatform, setAddPlatform] = useState('instagram');

  useEffect(() => {
    if (settings) {
      setForm({
        siteName: settings.siteName || '',
        tagline: settings.tagline || '',
        'contact.address': settings.contact?.address || '',
        'contact.mapUrl': settings.contact?.mapUrl || '',
        'contact.phone': settings.contact?.phone || '',
        'contact.email': settings.contact?.email || '',
        'contact.hours': settings.contact?.hours || '',
      });
      setLogoPreview(settings.logoUrl || null);
      // Migrate legacy object format to array if needed
      if (Array.isArray(settings.socialLinks)) {
        // Filter out any entries with missing platform
        setSocialLinks(settings.socialLinks.filter(l => l && l.platform));
      } else if (settings.socialLinks && typeof settings.socialLinks === 'object') {
        const migrated = Object.entries(settings.socialLinks)
          .filter(([platform, url]) => platform && typeof url === 'string' && url.trim())
          .map(([platform, url]) => ({ platform, url, enabled: true }));
        setSocialLinks(migrated);
      } else {
        setSocialLinks([]);
      }
      if (settings.typography) {
        setTypography({ ...DEFAULT_TYPOGRAPHY, ...settings.typography });
      }
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('socialLinks', JSON.stringify(socialLinks));
      fd.append('typography', JSON.stringify(typography));
      if (logoFile) fd.append('logo', logoFile);
      await api.put('/settings', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Site settings saved');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving settings');
    } finally { setLoading(false); }
  };

  const f = (k) => ({ value: form[k], onChange: e => setForm({ ...form, [k]: e.target.value }) });

  // Social link helpers
  const addLink = () => {
    if (socialLinks.find(l => l.platform === addPlatform)) {
      toast.info(`${getPlatformInfo(addPlatform).label} is already added`);
      return;
    }
    setSocialLinks(prev => [...prev, { platform: addPlatform, url: '', enabled: true }]);
  };

  const updateLink = (i, field, value) => {
    setSocialLinks(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  };

  const removeLink = (i) => {
    setSocialLinks(prev => prev.filter((_, idx) => idx !== i));
  };

  const usedPlatforms = socialLinks.map(l => l.platform);
  const availablePlatforms = PLATFORM_OPTIONS.filter(p => !usedPlatforms.includes(p.value));

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="font-serif text-2xl text-gray-800">Site Settings</h1>
        <p className="text-gray-500 text-sm">Manage your logo, site name, contact info and social links.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Logo */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <h2 className="font-semibold text-gray-700 text-sm mb-4">Company Logo</h2>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
              {logoPreview
                ? <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                : <span className="text-xs text-gray-400 text-center px-2">No logo</span>
              }
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload New Logo</label>
              <input type="file" accept="image/*"
                onChange={e => { setLogoFile(e.target.files[0]); setLogoPreview(e.target.files[0] ? URL.createObjectURL(e.target.files[0]) : logoPreview); }}
                className="text-sm text-gray-600" />
              <p className="text-xs text-gray-400 mt-1">PNG or SVG recommended.</p>
            </div>
          </div>
        </div>

        {/* Brand */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4">
          <h2 className="font-semibold text-gray-700 text-sm">Brand Identity</h2>
          <Field label="Site Name" hint="Displayed in browser tab and footer copyright">
            <Input {...f('siteName')} placeholder="House of Sample" />
          </Field>
          <Field label="Tagline" hint="Short brand tagline shown in footer and splash screen">
            <Input {...f('tagline')} placeholder="Focus On Quality" />
          </Field>
        </div>

        {/* Contact */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4">
          <h2 className="font-semibold text-gray-700 text-sm">Contact Information</h2>
          <Field label="Address" hint="Full business address shown on the contact page">
            <Input {...f('contact.address')} placeholder="206, GIDC Naroda, Ahmedabad..." />
          </Field>
          <Field label="Google Maps Embed URL" hint='Go to Google Maps → Share → Embed a map → copy only the src="..." value (starts with https://www.google.com/maps/embed?pb=...)'>
            <Input {...f('contact.mapUrl')} placeholder="https://www.google.com/maps/embed?pb=..." />
            {form['contact.mapUrl'] && form['contact.mapUrl'].includes('google.com/maps/embed') && (
              <div className="mt-2 w-full h-40 border border-gray-200 rounded overflow-hidden">
                <iframe title="Map preview" src={form['contact.mapUrl']}
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade" />
              </div>
            )}
            {form['contact.mapUrl'] && !form['contact.mapUrl'].includes('google.com/maps/embed') && (
              <p className="text-amber-600 text-xs mt-1">⚠ This doesn't look like a Google Maps embed URL. Make sure it starts with https://www.google.com/maps/embed?pb=</p>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone">
              <Input {...f('contact.phone')} placeholder="+91 85114 28123" />
            </Field>
            <Field label="Email">
              <Input type="email" {...f('contact.email')} placeholder="info@houseofsample.com" />
            </Field>
          </div>
          <Field label="Business Hours">
            <Input {...f('contact.hours')} placeholder="Mon–Sat: 9:00 AM – 6:00 PM" />
          </Field>
        </div>

        {/* Social Links */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-700 text-sm">Social Media Links</h2>
              <p className="text-xs text-gray-400 mt-0.5">Only links with a URL and enabled toggle will appear on the website.</p>
            </div>
          </div>

          {/* Existing links */}
          {socialLinks.length === 0 && (
            <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
              No social links added yet. Add one below.
            </div>
          )}

          <div className="space-y-3">
            {socialLinks.map((link, i) => {
              const info = getPlatformInfo(link.platform);
              return (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  {/* Platform icon + name */}
                  <div className="flex items-center gap-2 w-28 shrink-0">
                    <span style={{ color: info.color }}><PlatformIcon platform={link.platform} size={18} /></span>
                    <span className="text-xs font-semibold text-gray-700">{info.label}</span>
                  </div>

                  {/* URL input */}
                  <input
                    type="url"
                    value={link.url}
                    onChange={e => updateLink(i, 'url', e.target.value)}
                    placeholder={info.placeholder}
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-400 transition-colors bg-white"
                  />

                  {/* Enable toggle */}
                  <label className="flex items-center gap-1.5 cursor-pointer shrink-0" title="Show on website">
                    <div
                      onClick={() => updateLink(i, 'enabled', !link.enabled)}
                      className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${link.enabled ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${link.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-xs text-gray-500">{link.enabled ? 'On' : 'Off'}</span>
                  </label>

                  {/* Remove */}
                  <button type="button" onClick={() => removeLink(i)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                    title="Remove">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Add new platform */}
          {availablePlatforms.length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
              <select
                value={addPlatform}
                onChange={e => setAddPlatform(e.target.value)}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-400 bg-white">
                {availablePlatforms.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <button type="button" onClick={addLink}
                className="flex items-center gap-1.5 px-4 py-2 bg-yellow-500 text-white text-sm font-semibold rounded-lg hover:bg-yellow-600 transition-colors shrink-0">
                <Plus size={14} /> Add
              </button>
            </div>
          )}
          {availablePlatforms.length === 0 && (
            <p className="text-xs text-gray-400 text-center pt-2 border-t border-gray-100">All available platforms have been added.</p>
          )}
        </div>

        {/* Typography */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-700 text-sm">Typography</h2>
              <p className="text-xs text-gray-400 mt-0.5">Font family, colour and size applied across the entire website.</p>
            </div>
            <button type="button"
              onClick={() => setTypography(DEFAULT_TYPOGRAPHY)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-yellow-600 border border-gray-200 hover:border-yellow-400 px-3 py-1.5 rounded-lg transition-colors">
              <RotateCcw size={12} /> Reset to Default
            </button>
          </div>

          {/* Live preview */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-1">
            <p style={{ fontFamily: `"${typography.headingFont}", serif`, color: typography.baseColor, fontSize: `${Number(typography.baseFontSize) * 1.4}px`, fontStyle: typography.fontStyle }}>
              House of Sample — Heading Preview
            </p>
            <p style={{ fontFamily: `"${typography.bodyFont}", sans-serif`, color: typography.baseColor, fontSize: `${typography.baseFontSize}px`, fontStyle: typography.fontStyle }}>
              Body text preview — Premium sample solutions for the textile industry.
            </p>
            <p style={{ fontFamily: `"${typography.bodyFont}", sans-serif`, color: typography.accentColor, fontSize: `${typography.baseFontSize}px`, fontWeight: 600 }}>
              Accent colour text — Focus On Quality
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Body Font">
              <select value={typography.bodyFont} onChange={e => setTypography(t => ({ ...t, bodyFont: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 bg-white">
                {BODY_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Heading Font">
              <select value={typography.headingFont} onChange={e => setTypography(t => ({ ...t, headingFont: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 bg-white">
                {HEADING_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <ColorSwatchPicker
              label="Base Text Colour"
              hint="Main body text colour"
              value={typography.baseColor}
              onChange={v => setTypography(t => ({ ...t, baseColor: v }))}
            />
            <ColorSwatchPicker
              label="Accent Colour"
              hint="Gold/brand colour"
              value={typography.accentColor}
              onChange={v => setTypography(t => ({ ...t, accentColor: v }))}
            />
            <Field label="Base Font Size (px)" hint="Body text size">
              <input type="number" min="12" max="22" value={typography.baseFontSize}
                onChange={e => setTypography(t => ({ ...t, baseFontSize: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400" />
            </Field>
          </div>

          <Field label="Font Style">
            <div className="flex gap-3">
              {['normal', 'italic'].map(s => (
                <button key={s} type="button"
                  onClick={() => setTypography(t => ({ ...t, fontStyle: s }))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all capitalize
                    ${typography.fontStyle === s ? 'border-yellow-400 bg-yellow-50 text-yellow-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  style={{ fontStyle: s }}>
                  {s === 'italic' ? <em>Italic</em> : 'Normal'}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <FormActions onCancel={() => {}} loading={loading} submitLabel="Save Settings" />
      </form>
    </div>
  );
}
